import { takeEvery, call, put, select } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import * as assetConstants from '../constants/assets';
import { getErrorMessage } from '../helpers/Util';

import { moveAsset, deleteAsset, updateAsset, publishAsset } from './assetsSaga';

function* refreshAssetsList(options = {}) {
    // Calculate delay based on number of assets - minimum 1 second, plus additional time for bulk operations
    // For every 10 assets, add 500ms of delay, up to a reasonable maximum
    const { assetCount = 0 } = options;
    const baseDelay = 1000;
    const additionalDelay = Math.min(Math.floor(assetCount / 10) * 500, 5000);
    const delayTime = baseDelay + additionalDelay;
    
    yield delay(delayTime);
    
    const assetsState = yield select(state => state.assets);
    
    yield put({
        type: assetConstants.FETCH_ASSETS_REQUEST,
        payload: {
            bucketId: assetsState.bucketId,
            application: assetsState.application,
            order: assetsState.order,
            orderDirection: assetsState.orderDirection,
        },
    });
}

function* copyAssetsBatchAction(action) {
    const { assets, additionalData } = action.payload;
    const { toBucket } = additionalData;
    
    if (!toBucket) {
        yield put({
            type: assetConstants.COPY_ASSETS_FAILURE,
            error: true,
            payload: {
                error: 'Missing required data for copying assets',
            },
        });
        return;
    }

    try {
        for (const asset of assets) {
            yield call(moveAsset, {
                payload: {
                    asset,
                    toBucket,
                    isCopy: true,
                },
            });
        }
        
        yield put({
            type: assetConstants.COPY_ASSETS_SUCCESS,
            payload: {
                assets,
                toBucket,
            },
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.COPY_ASSETS_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }
}

function* duplicateAssetsBatchAction(action) {
    const { assets } = action.payload;

    try {
        for (const asset of assets) {
            yield call(moveAsset, {
                payload: {
                    asset,
                    toBucket: { id: asset.ownerId },
                    isDuplicate: true,
                },
            });
        }
        
        yield put({
            type: assetConstants.DUPLICATE_ASSETS_SUCCESS,
            payload: {
                assets,
            },
        });

        yield call(refreshAssetsList, { assetCount: assets.length });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.DUPLICATE_ASSETS_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }
}

function* moveAssetsBatchAction(action) {
    const { assets, additionalData } = action.payload;
    const { toBucket } = additionalData;
    
    if (!toBucket) {
        yield put({
            type: assetConstants.MOVE_ASSETS_FAILURE,
            error: true,
            payload: {
                error: 'Missing required data for moving assets',
            },
        });
        return;
    }

    try {
        for (const asset of assets) {
            yield call(moveAsset, {
                payload: {
                    asset,
                    toBucket,
                    isCopy: false,
                },
            });
        }
        
        yield put({
            type: assetConstants.MOVE_ASSETS_SUCCESS,
            payload: {
                assets,
                toBucket,
            },
        });

        yield call(refreshAssetsList, { assetCount: assets.length });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.MOVE_ASSETS_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }
}

function* deleteAssetsBatchAction(action) {
    const { assets } = action.payload;
    
    try {
        for (const asset of assets) {
            yield call(deleteAsset, { 
                payload: { 
                    asset 
                } 
            });
        }
        
        yield put({
            type: assetConstants.DELETE_ASSETS_SUCCESS,
            payload: {
                assets,
            },
        });

        yield call(refreshAssetsList, { assetCount: assets.length });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.DELETE_ASSETS_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }
}

export function* tagAssetsBatchAction({ payload }) {
    const { assets, additionalData } = payload;
    const { tags, mode } = additionalData;
    const isAddMode = mode === 'add';

    if (!tags || !tags.length) {
        yield put({
            type: assetConstants.TAG_ASSETS_FAILURE,
            payload: {
                error: 'No tags provided'
            }
        });
        return;
    }

    try {
        for (const asset of assets) {
            try {
                const existingTags = asset.tags || [];
                let updatedTags;

                // If the asset has no tags and we're not adding tags, skip the asset
                if(existingTags.length === 0 && !isAddMode) {
                    continue;  
                }
                
                if (isAddMode) {
                    // Add mode: combine existing and new tags
                    updatedTags = [...new Set([...existingTags, ...tags])];
                } else {
                    // Remove mode: filter out the selected tags
                    updatedTags = existingTags.filter(tag => !tags.includes(tag));
                }

                yield call(updateAsset, {
                    payload: {
                        asset,
                        updatedAsset: { tags: updatedTags }
                    }
                });
            } catch (error) {
                console.error(`Failed to update tags for asset ${asset.id}:`, error);
            }
        }

        yield put({
            type: assetConstants.TAG_ASSETS_SUCCESS,
            payload: {
                assets,
            }
        });

        yield call(refreshAssetsList, { assetCount: assets.length });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.TAG_ASSETS_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }
}

function* publishAssetsBatchAction(action) {
    const { assets } = action.payload;
    
    try {
        for (const asset of assets) {
            yield call(publishAsset, { 
                payload: { 
                    asset
                } 
            });
        }
        
        yield put({
            type: assetConstants.PUBLISH_ASSETS_SUCCESS,
            payload: {
                assets,
            },
        });

        yield call(refreshAssetsList, { assetCount: assets.length });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.PUBLISH_ASSETS_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }
}

export function* watchBatchActions() {
    yield takeEvery(assetConstants.COPY_ASSETS_REQUEST, copyAssetsBatchAction);
    yield takeEvery(assetConstants.DUPLICATE_ASSETS_REQUEST, duplicateAssetsBatchAction);
    yield takeEvery(assetConstants.MOVE_ASSETS_REQUEST, moveAssetsBatchAction);
    yield takeEvery(assetConstants.DELETE_ASSETS_REQUEST, deleteAssetsBatchAction);
    yield takeEvery(assetConstants.TAG_ASSETS_REQUEST, tagAssetsBatchAction);
    yield takeEvery(assetConstants.PUBLISH_ASSETS_REQUEST, publishAssetsBatchAction);
} 


// WEBPACK FOOTER //
// ./src/sagas/batchAssetsSaga.js